import { useEffect, useState } from 'react';
import {
  describeError,
  fetchRevisionIssues,
  revisionCsvUrl,
  SEVERITY_LABELS,
  type Project,
  type RevisionIssuesResponse,
  type RevisionProjectRow,
  type Severity,
} from '../api.js';
import { formatDate, formatHoursTotal, issuesCountPlural } from '../format.js';

/**
 * Правило раскраски объясняется прямо на экране. Цвет без подписи читается как
 * оценка человека, а это список задач, а не людей.
 */
const LEGEND: Record<Severity, { label: string; hint: string }> = {
  red: {
    label: SEVERITY_LABELS.red,
    hint: 'время списано, задача не двигалась больше года, исполнителя нет или он уволился',
  },
  orange: {
    label: SEVERITY_LABELS.orange,
    hint: 'исполнителя нет или он уволился, часов не списано — чистка очереди',
  },
  yellow: {
    label: SEVERITY_LABELS.yellow,
    hint: 'задача не двигалась больше двух месяцев — в работе она уже не находится',
  },
  none: { label: SEVERITY_LABELS.none, hint: 'задачу трогали за последние два месяца' },
};

const ORDER: Severity[] = ['red', 'orange', 'yellow', 'none'];

export interface RevisionIssuesProps {
  projects: Project[];
  projectIds: string[];
  onProjectsChange: (ids: string[]) => void;
  severity: Severity | undefined;
  onSeverityChange: (severity: Severity | undefined) => void;
  /** Меняется после синхронизации: список перечитывается без правки фильтров. */
  dataVersion: number;
}

export function RevisionIssues(props: RevisionIssuesProps) {
  const { projectIds, severity, dataVersion } = props;
  const toggleProject = (projectId: string) =>
    props.onProjectsChange(
      projectIds.includes(projectId)
        ? projectIds.filter((id) => id !== projectId)
        : [...projectIds, projectId],
    );
  const [data, setData] = useState<RevisionIssuesResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [csvOpen, setCsvOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);

    fetchRevisionIssues({ projectIds, ...(severity ? { severity } : {}) }, controller.signal).then(
      (response) => {
        setData(response);
        setLoading(false);
      },
      (cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
        setLoading(false);
      },
    );

    return () => controller.abort();
  }, [projectIds, severity, dataVersion]);

  // Проекты берём из ответа: он считает их по всему найденному, а не по
  // показанной тысяче строк, и не сужается выбором проекта — иначе после клика
  // в фильтре осталась бы одна кнопка.
  const activeCategory = new Set(
    props.projects.filter((project) => project.isActiveCategory).map((project) => project.jiraId),
  );
  const byCategory = {
    active: (data?.projects ?? []).filter((project) => activeCategory.has(project.projectId)),
    inactive: (data?.projects ?? []).filter((project) => !activeCategory.has(project.projectId)),
  };

  return (
    <section className="revision">
      <div className="severity-cards">
        {ORDER.map((level) => {
          const bucket = data?.totals.find((total) => total.severity === level);
          const selected = severity === level;
          return (
            <button
              key={level}
              type="button"
              className={`severity-card sev-${level}${selected ? ' selected' : ''}`}
              onClick={() => props.onSeverityChange(selected ? undefined : level)}
              title={LEGEND[level].hint}
            >
              <span className="severity-name">{LEGEND[level].label}</span>
              <span className="severity-count">{bucket?.issues ?? 0}</span>
              <span className="severity-hours">
                {bucket && bucket.seconds > 0
                  ? `${formatHoursTotal(bucket.seconds)} ч`
                  : 'без часов'}
              </span>
            </button>
          );
        })}
      </div>

      <p className="muted small">
        {severity
          ? LEGEND[severity].hint
          : 'Нажмите на карточку, чтобы оставить только эти задачи.'}{' '}
        Часы взяты с самой задачи в Jira, поэтому включают время, списанное до начала нашей
        выгрузки, — в наших worklog его нет.
      </p>

      {data && data.projects.length > 0 && (
        <div className="revision-projects">
          <div className="chips">
            <button
              type="button"
              className={`chip${projectIds.length === 0 ? ' chip-on' : ''}`}
              onClick={() => props.onProjectsChange([])}
            >
              все проекты
            </button>
            {projectIds.length > 0 && (
              <span className="muted small">выбрано {projectIds.length}</span>
            )}
          </div>

          <ProjectRow
            label="Активные"
            hint="категория Active в Jira"
            projects={byCategory.active}
            selected={projectIds}
            onToggle={toggleProject}
          />
          <ProjectRow
            label="Неактивные"
            hint={inactiveHint(byCategory.inactive)}
            projects={byCategory.inactive}
            selected={projectIds}
            onToggle={toggleProject}
            flagged
          />
        </div>
      )}

      <div className="revision-export">
        <button type="button" className="chip" onClick={() => setCsvOpen(!csvOpen)}>
          Скачать CSV {csvOpen ? '▴' : '▾'}
        </button>
        {csvOpen && (
          <div className="export-menu">
            <a href={revisionCsvUrl({ projectIds })} onClick={() => setCsvOpen(false)}>
              Весь список{' '}
              <span className="muted">
                {data ? `— ${data.totals.reduce((sum, total) => sum + total.issues, 0)} задач` : ''}
              </span>
            </a>
            {ORDER.map((level) => {
              const bucket = data?.totals.find((total) => total.severity === level);
              return (
                <a
                  key={level}
                  href={revisionCsvUrl({ projectIds, severity: level })}
                  onClick={() => setCsvOpen(false)}
                >
                  {LEGEND[level].label} <span className="muted">— {bucket?.issues ?? 0} задач</span>
                </a>
              );
            })}
            <div className="muted small">
              Файл идёт целиком, без ограничения на число строк, и с пустой колонкой «Решение» под
              пометки «закрыть / переназначить / оставить». Фильтр по проекту учитывается,
              {projectIds.length ? ' сейчас он включён' : ' сейчас выбраны все проекты'}.
            </div>
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {loading && !data && <div className="empty">Загрузка…</div>}

      {data && (
        <>
          <div className="matrix-scroll">
            <table className="matrix revision-table">
              <thead>
                <tr>
                  <th>Задача</th>
                  <th>Проект</th>
                  <th>Тема</th>
                  <th>Статус</th>
                  <th>Исполнитель</th>
                  <th>Завёл</th>
                  <th className="num">Часов</th>
                  <th className="num">Остаток</th>
                  <th className="num">Стоит, дней</th>
                  <th>Изменена</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.issueKey} className={`sev-row-${row.severity}`}>
                    <td>
                      {row.url ? (
                        <a href={row.url} target="_blank" rel="noreferrer">
                          {row.issueKey}
                        </a>
                      ) : (
                        row.issueKey
                      )}
                    </td>
                    <td title={row.projectName}>{row.projectKey}</td>
                    <td className="wrap" title={row.summary}>
                      {row.summary}
                    </td>
                    <td>{row.status ?? '—'}</td>
                    <td className={row.assigneeActive === false ? 'left-company' : undefined}>
                      {row.assigneeName ?? '— нет —'}
                    </td>
                    <td className={row.reporterActive === false ? 'left-company' : undefined}>
                      {row.reporterName ?? '—'}
                    </td>
                    <td className="num">{formatHoursTotal(row.seconds)}</td>
                    <td className="num">{formatHoursTotal(row.remainingSeconds)}</td>
                    <td className="num">{row.staleDays ?? '—'}</td>
                    <td>{row.updatedAt ? formatDate(row.updatedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="muted small">
            Показано {data.shown} из {data.found}
            {data.truncated && ' — список обрезан, сузьте фильтр по проекту'}. Уволившиеся отмечены
            серым.
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Подсказка считается по данным, а не зашита числом: категория Inactive — метка
 * устаревших метаданных, и убедительнее всего это показывает объём работы,
 * который она задевает. Зашитое число устарело бы уже к следующей синхронизации.
 */
function inactiveHint(projects: RevisionProjectRow[]): string {
  const issues = projects.reduce((sum, project) => sum + project.issues, 0);
  const seconds = projects.reduce((sum, project) => sum + project.seconds, 0);
  return (
    'категория Inactive в Jira — метка устаревших метаданных, а не признак того, что работы ' +
    `не было: здесь ${issues} незакрытых ${issuesCountPlural(issues)} и ${formatHoursTotal(seconds)} ч, ` +
    'они учитываются наравне'
  );
}

interface ProjectRowProps {
  label: string;
  hint: string;
  projects: RevisionProjectRow[];
  selected: string[];
  onToggle: (projectId: string) => void;
  /** Категория Inactive помечается точкой — тем же знаком, что и в матрице часов. */
  flagged?: boolean;
}

function ProjectRow({ label, hint, projects, selected, onToggle, flagged }: ProjectRowProps) {
  if (projects.length === 0) return null;

  return (
    <div className="chip-group">
      <div className="chip-group-label" title={hint}>
        {label} <span className="muted">({projects.length})</span>
      </div>
      <div className="chips">
        {projects.map((project) => (
          <button
            key={project.projectId}
            type="button"
            className={`chip${selected.includes(project.projectId) ? ' chip-on' : ''}${
              flagged ? ' chip-flagged' : ''
            }`}
            onClick={() => onToggle(project.projectId)}
            title={`${project.projectName} — ${project.issues} задач`}
          >
            {project.projectKey}
            <span className="chip-count">{project.issues}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
